import { computed, signal } from '@angular/core';
import { decodeEnvelope, debugFlowOf, findingsReport, type FlowItem } from './sync-debug-view.util';
import {
  detectFindings, syncMarksOf, type Finding, type FindingKind, type SyncMark,
} from './sync-findings.util';
import { inspectSpan, type Inspection } from './sync-inspector.util';
import type { SessionReport } from './tracking-demo.models';
import { mmss, type TimedTurn, type TranscriptModel } from './transcript-timing.util';

/** A badge on a turn card while debugging. */
export interface TurnBadge { text: string; tone: 'ok' | 'warn'; }

/**
 * Everything the transcript player's debug mode shows, as signals: the host's
 * marks, the detected findings, the per-leg waveforms, the selected finding's
 * inspection, and the row geometry derived from them. Owned by the player; the
 * maths lives in the sync utils.
 */
export class SyncDebugState {
  readonly marks = signal<SyncMark[]>([]);
  readonly findings = signal<Finding[]>([]);
  readonly envHuman = signal<Uint8Array>(new Uint8Array(0));
  readonly envAgent = signal<Uint8Array>(new Uint8Array(0));
  readonly envHz = signal(10);
  /** Play a gap on repeat so it can be listened to closely. */
  readonly loopSpan = signal(false);
  /** The finding whose inspector is open. */
  readonly selected = signal<Finding | null>(null);
  readonly kinds: FindingKind[] = ['untranscribed', 'gated_onset', 'talked_over', 'phantom', 'late'];
  private readonly model = signal<TranscriptModel | null>(null);

  constructor(
    private readonly rows: () => TimedTurn[],
    private readonly durationSec: () => number,
    private readonly on: () => boolean,
  ) {}

  /** Turns, with gap cards merged in while debugging. */
  readonly flow = computed<FlowItem[]>(() => this.on()
    ? debugFlowOf(this.rows(), this.findings())
    : this.rows().map((row, index) => ({ kind: 'turn', row, index })));

  /** Turn windows as blocks on the Transcript row (measured solid, estimated dashed). */
  readonly turnBlocks = computed(() => this.rows()
    .filter((r) => r.start != null && r.end != null)
    .map((r) => ({ row: r, ...this.box(r.start!, r.end!) })));
  readonly findingBlocks = computed(() => this.findings().map((f) => ({ f, ...this.box(f.t0, f.t1) })));
  /** Marks as blocks; a point mark carries its label only when the previous
   *  labelled point is far enough away for the text to fit (long calls pack them). */
  readonly markBlocks = computed(() => {
    let lastLabelPct = -Infinity;
    return this.marks().map((m) => {
      const leftPct = this.pct(m.t);
      const widthPct = m.t1 != null ? Math.max(0.15, this.pct(m.t1) - this.pct(m.t)) : 0;
      const label = widthPct === 0 && leftPct - lastLabelPct >= SyncDebugState.LABEL_GAP_PCT;
      if (label) lastLabelPct = leftPct;
      return { m, leftPct, widthPct, label };
    });
  });
  private static readonly LABEL_GAP_PCT = 6;
  /** Echo-gate-closed spans, hatched over the Human row. */
  readonly gateBlocks = computed(() => this.marks()
    .filter((m) => m.kind === 'gate' && m.t1 != null)
    .map((m) => ({ m, ...this.box(m.t, m.t1!) })));
  readonly gateClosedSec = computed(() => this.gateBlocks().reduce((n, b) => n + (b.m.t1! - b.m.t), 0));
  readonly hasWaveform = computed(() => this.envHuman().length + this.envAgent().length > 0);
  /** Tall rows with the wave behind the bars: whenever a wave exists, and always in debug. */
  readonly waveRows = computed(() => this.on() || this.hasWaveform());

  readonly inspection = computed<Inspection | null>(() => {
    const f = this.selected();
    const m = this.model();
    if (!f || !m) return null;
    return inspectSpan(f, m, this.marks(), { hz: this.envHz(), human: this.envHuman(), agent: this.envAgent() });
  });

  load(report: SessionReport, model: TranscriptModel): void {
    this.model.set(model);
    this.marks.set(syncMarksOf(report));
    this.findings.set(detectFindings(model, this.marks()));
    this.envHz.set(report.recording?.envelope?.hz || 10);
    this.envHuman.set(decodeEnvelope(report.recording?.envelope?.human));
    this.envAgent.set(decodeEnvelope(report.recording?.envelope?.agent));
    this.selected.set(null);
  }

  countOf(kind: FindingKind): number {
    return this.findings().filter((f) => f.kind === kind).length;
  }

  report(sessionId: string, only?: Finding): string {
    return findingsReport(sessionId, only ? [only] : this.findings());
  }

  /** The finding that overlaps one bar, if any (drives the bar's outline). */
  flagAt(ai: boolean, at: number, end: number): Finding | null {
    return this.findings().find((f) => (f.kind === 'talked_over') === ai && f.t0 < end && f.t1 > at) ?? null;
  }

  /** What a turn card says about itself while debugging. */
  badgeFor(row: TimedTurn): TurnBadge | null {
    if (row.start == null || row.end == null) return null;
    if (row.ai) {
      const over = this.findings().find((f) => f.kind === 'talked_over' && f.t0 < row.end! && f.t1 > row.start!);
      return over ? { text: `talked over ${over.against ?? 'a human'} at ${mmss(over.t0)}`, tone: 'warn' } : null;
    }
    return row.measured
      ? { text: `matched ${mmss(row.start)} – ${mmss(row.end)}`, tone: 'ok' }
      : { text: `estimated ${mmss(row.start)} – ${mmss(row.end)}`, tone: 'warn' };
  }

  private box(t0: number, t1: number): { leftPct: number; widthPct: number } {
    return { leftPct: this.pct(t0), widthPct: Math.max(0.4, this.pct(t1) - this.pct(t0)) };
  }

  private pct(sec: number): number {
    const dur = this.durationSec();
    return dur ? Math.min(100, Math.max(0, (sec / dur) * 100)) : 0;
  }
}
